import type { CustomerAddress } from './api';

function join(parts: Array<string | null | undefined>, separator = ' '): string {
  return parts
    .filter((part): part is string => part != null && part.trim() !== '')
    .map((part) => part.trim())
    .join(separator);
}

/**
 * The two-line formatted postal block for a customer address, mirroring the backend `FormattedAddress`
 * (line 1: street + building[/unit], or the locality + building[/unit] when there is no street; line 2:
 * postal code + post town). When a street is present and the locality differs from the post town, the
 * locality is appended to the first line (`street building/unit, locality`). Blank lines drop out.
 */
export function formatAddressLines(address: CustomerAddress): string[] {
  const home = join([address.buildingNumber, address.unitNumber], '/');
  let firstLine: string;
  if (address.street == null) {
    firstLine = join([address.locality, home]);
  } else {
    const streetAndHome = join([address.street, home]);
    const localityMatchesPostTown =
      address.locality.trim().toLowerCase() === address.postTown.trim().toLowerCase();
    firstLine = localityMatchesPostTown ? streetAndHome : join([streetAndHome, address.locality], ', ');
  }
  return [firstLine, join([address.postalCode, address.postTown])].filter((line) => line !== '');
}

/**
 * Normalizes a Polish postal code to the `XX-XXX` form (`12345` → `12-345`). Anything already
 * containing a separator, or not five bare digits, passes through trimmed (the server validates).
 */
export function normalizePlPostalCode(raw: string): string {
  const trimmed = raw.trim();
  return /^\d{5}$/.test(trimmed) ? `${trimmed.slice(0, 2)}-${trimmed.slice(2)}` : trimmed;
}
